"use client";

import {
  AssistantRuntimeProvider,
  type ChatModelAdapter,
  type ChatModelRunResult,
  useLocalRuntime,
} from "@assistant-ui/react";
import {
  createContext,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { toast } from "sonner";

import {
  HITL_PAUSE_LABEL,
  THINKING_LABEL,
  activityLabel,
} from "@/app/_components/assistant-ui/activity-labels";
import type {
  ChatAdapter,
  MarbleThreadMessage,
  PendingAction,
  TurnEvent,
} from "@/app/_components/assistant-ui/types";

interface PendingActionContextValue {
  pendingAction: PendingAction | null;
  clearPendingAction: () => void;
}

const PendingActionContext = createContext<PendingActionContextValue>({
  pendingAction: null,
  clearPendingAction: () => {},
});

export function useMarbleAssistantPendingAction(): PendingActionContextValue {
  return useContext(PendingActionContext);
}

interface MarbleAssistantRuntimeProviderProps {
  adapter: ChatAdapter;
  /**
   * Explicit initial messages to seed the thread with. When provided,
   * `adapter.loadHistory` is NOT called - the caller is responsible for
   * loading history themselves. Useful when the surrounding page already
   * needs the history payload (e.g. to extract metadata).
   */
  initialMessages?: readonly MarbleThreadMessage[];
  /** Skip calling adapter.loadHistory on mount (e.g. after a manual reset). */
  skipHistoryLoad?: boolean;
  children: ReactNode;
}

/**
 * Wraps `useLocalRuntime` with an async-generator `ChatModelAdapter` that
 * delegates to the supplied `ChatAdapter`. Each yielded
 * `ChatModelRunResult` updates the assistant message in place — that's
 * what gives the UI a Cursor-style live activity label
 * ("Searching the knowledge base...", "Reading past notes...") that
 * gets replaced by the final assistant text on `completed`.
 *
 * If a "Confirm" or "Cancel" reply lands while there is an outstanding
 * `pendingAction` AND the adapter exposes `confirmAction`, the message
 * is routed through `confirmAction` instead of `sendMessage` so adapters
 * can use the explicit HITL API where one exists.
 */
export function MarbleAssistantRuntimeProvider({
  adapter,
  initialMessages: explicitInitialMessages,
  skipHistoryLoad,
  children,
}: MarbleAssistantRuntimeProviderProps) {
  const hasExplicitInitialMessages = explicitInitialMessages !== undefined;
  const hasLoadHistory = !!adapter.loadHistory && !hasExplicitInitialMessages;
  const [loadedInitialMessages, setLoadedInitialMessages] = useState<
    MarbleThreadMessage[]
  >([]);
  const [historyLoaded, setHistoryLoaded] = useState(
    !!skipHistoryLoad || !hasLoadHistory,
  );
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(() =>
    _lastPendingAction(explicitInitialMessages),
  );

  useEffect(() => {
    if (skipHistoryLoad || hasExplicitInitialMessages || !adapter.loadHistory) {
      return;
    }
    let cancelled = false;
    adapter
      .loadHistory()
      .then((messages) => {
        if (cancelled) return;
        setLoadedInitialMessages(messages);
        setPendingAction(_lastPendingAction(messages));
      })
      .catch(() => {
        if (!cancelled) {
          toast.error("Failed to load chat history");
        }
      })
      .finally(() => {
        if (!cancelled) setHistoryLoaded(true);
      });
    return () => {
      cancelled = true;
    };
  }, [adapter, skipHistoryLoad, hasExplicitInitialMessages]);

  const chatModelAdapter = useMemo<ChatModelAdapter>(
    () => ({
      async *run({ messages }) {
        const lastUserMessage = [...messages]
          .reverse()
          .find((m) => m.role === "user");

        if (!lastUserMessage) {
          yield _textResult("I didn't catch that. Could you try again?");
          return;
        }

        const userText = _extractText(lastUserMessage.content);
        const history = _toMarbleHistory(messages);
        const isConfirm = userText.trim().toLowerCase() === "confirm";
        const isCancel = userText.trim().toLowerCase() === "cancel";

        let stream: AsyncIterable<TurnEvent>;
        try {
          stream =
            pendingAction && adapter.confirmAction && (isConfirm || isCancel)
              ? adapter.confirmAction(
                  isConfirm,
                  isCancel ? "User clicked Cancel" : undefined,
                )
              : adapter.sendMessage(userText, history);
        } catch (error) {
          const message =
            error instanceof Error ? error.message : "Something went wrong";
          toast.error(message);
          yield _textResult(message);
          return;
        }

        try {
          let lastActivityText = THINKING_LABEL;
          let lastActivityYieldTime = 0;

          for await (const evt of stream) {
            const next = _eventToRunResult(evt, lastActivityText);
            if (next.kind === "yield") {
              const nextText = next.activityText;
              if (nextText !== undefined && nextText !== lastActivityText) {
                // New activity label — hold the previous one for at least
                // MIN_ACTIVITY_MS so it doesn't flash by unreadably fast.
                const elapsed = Date.now() - lastActivityYieldTime;
                if (lastActivityYieldTime > 0 && elapsed < MIN_ACTIVITY_MS) {
                  await _sleep(MIN_ACTIVITY_MS - elapsed);
                }
                lastActivityYieldTime = Date.now();
                lastActivityText = nextText;
              }
              yield next.result;
              continue;
            }
            if (next.kind === "completed") {
              setPendingAction(next.pendingAction ?? null);
              yield next.result;
              return;
            }
            if (next.kind === "error") {
              toast.error("Failed to send message. Please try again.");
              yield _textResult(next.message);
              return;
            }
          }
        } catch (error) {
          if (process.env.NODE_ENV !== "production") {
            // eslint-disable-next-line no-console
            console.error("MarbleAssistant stream failed:", error);
          }
          toast.error("Failed to send message. Please try again.");
          yield _textResult("Sorry, something went wrong. Please try again.");
        }
      },
    }),
    [adapter, pendingAction],
  );

  const seedMessages = hasExplicitInitialMessages
    ? (explicitInitialMessages ?? [])
    : historyLoaded
      ? loadedInitialMessages
      : [];

  const runtime = useLocalRuntime(chatModelAdapter, {
    initialMessages: seedMessages.map(_toThreadMessageLike),
  });

  const pendingActionValue = useMemo<PendingActionContextValue>(
    () => ({
      pendingAction,
      clearPendingAction: () => setPendingAction(null),
    }),
    [pendingAction],
  );

  return (
    <AssistantRuntimeProvider runtime={runtime}>
      <PendingActionContext.Provider value={pendingActionValue}>
        {children}
      </PendingActionContext.Provider>
    </AssistantRuntimeProvider>
  );
}

type EventResult =
  | {
      kind: "yield";
      result: ChatModelRunResult;
      activityText?: string;
    }
  | {
      kind: "completed";
      result: ChatModelRunResult;
      pendingAction: PendingAction | undefined;
    }
  | {
      kind: "error";
      message: string;
    };

function _eventToRunResult(
  evt: TurnEvent,
  lastActivityText: string,
): EventResult {
  switch (evt.type) {
    case "model_thinking":
      return {
        kind: "yield",
        result: _textResult(THINKING_LABEL),
        activityText: THINKING_LABEL,
      };
    case "tool_start": {
      const text = activityLabel({
        toolName: evt.toolName,
        displayName: evt.displayName,
      });
      return {
        kind: "yield",
        result: _textResult(text),
        activityText: text,
      };
    }
    case "tool_end":
      // Hold the most recent activity label until the next event arrives.
      // (Switching to "Thinking..." here would cause a flicker if the
      // model immediately runs another tool.)
      return {
        kind: "yield",
        result: _textResult(lastActivityText),
      };
    case "hitl_pause": {
      const text = HITL_PAUSE_LABEL({
        toolName: evt.toolName,
        displayName: evt.displayName,
      });
      return {
        kind: "yield",
        result: _textResult(text),
        activityText: text,
      };
    }
    case "completed":
      return {
        kind: "completed",
        result: _textResult(evt.text || "Done."),
        pendingAction: evt.pendingAction,
      };
    case "error":
      return { kind: "error", message: evt.message };
  }
}

/** Minimum time (ms) an activity label stays visible before being replaced. */
const MIN_ACTIVITY_MS = 1500;

const _sleep = (ms: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, ms));

function _textResult(text: string): ChatModelRunResult {
  return {
    content: [{ type: "text" as const, text }],
  };
}

function _extractText(content: unknown): string {
  if (typeof content === "string") return content;
  if (!Array.isArray(content)) return "";
  return content
    .filter(
      (part): part is { type: string; text: string } =>
        typeof part === "object" &&
        part !== null &&
        "type" in part &&
        (part as { type: unknown }).type === "text" &&
        "text" in part,
    )
    .map((part) => part.text)
    .join("");
}

function _toMarbleHistory(
  messages: ReadonlyArray<{
    role: string;
    content: unknown;
    id?: string;
    createdAt?: Date;
  }>,
): MarbleThreadMessage[] {
  return messages
    .filter(
      (m): m is typeof m & { role: "user" | "assistant" } =>
        m.role === "user" || m.role === "assistant",
    )
    .map((m, idx) => ({
      id: m.id ?? `history_${idx}`,
      role: m.role,
      content: _extractText(m.content),
      createdAt: m.createdAt ?? new Date(),
    }));
}

function _toThreadMessageLike(msg: MarbleThreadMessage): {
  role: "user" | "assistant";
  content: string;
  id: string;
  createdAt: Date;
} {
  return {
    role: msg.role,
    content: msg.content,
    id: msg.id,
    createdAt: msg.createdAt,
  };
}

function _lastPendingAction(
  messages: readonly MarbleThreadMessage[] | undefined,
): PendingAction | null {
  if (!messages) return null;
  const lastAssistant = [...messages]
    .reverse()
    .find((m) => m.role === "assistant");
  return lastAssistant?.pendingAction ?? null;
}
